/**
 * MAP_PERSONAS — single source of truth for how each mappable persona looks and
 * links on the /admin/map location map. Keyed by the backend's `MAP_PERSONA_KEYS`
 * (@truecare/schemas), so backend + frontend agree on which personas are
 * mappable. Adding a persona later = one entry here (drives the filter toggle,
 * the marker shape/colour, the legend, and the detail link) + the shared key.
 *
 * Distinct SHAPE per persona (not colour alone) so the map is readable at a
 * glance and for colour-blind operators. Colours are explicit here (SVG markers,
 * not CSS — a design-token pass can fold them in later).
 */
import type { MapPersonaKey } from '@truecare/schemas';
import { MAP_PERSONA_KEYS } from '@truecare/schemas';

export type MarkerShape = 'person' | 'building' | 'house';

export interface MapPersona {
  key: MapPersonaKey;
  label: string;
  color: string;
  shape: MarkerShape;
  /** Small pixel offset applied to this persona's individual pins so that
   *  entities at the SAME coordinate (e.g. a facility and a caregiver at one
   *  address) fan out instead of stacking and hiding each other. */
  pinOffset: [number, number];
  /** Admin detail route for a point's `linkId`. */
  detailPath: (linkId: string) => string;
}

export const MAP_PERSONAS: readonly MapPersona[] = [
  {
    key: 'caregiver',
    label: 'Care Professionals',
    color: '#0d9488', // teal-600
    shape: 'person',
    pinOffset: [0, 0],
    detailPath: (id) => `/caregivers/${id}`,
  },
  {
    key: 'facility',
    label: 'Facilities',
    color: '#2563eb', // blue-600
    shape: 'building',
    pinOffset: [11, -1],
    detailPath: (id) => `/facilities/${id}`,
  },
  {
    key: 'homecare',
    label: 'Homecare',
    color: '#d97706', // amber-600
    shape: 'house',
    pinOffset: [-11, -1],
    detailPath: (id) => `/homecare-clients/${id}`,
  },
];

// Assert the style table covers exactly the shared key list (drift guard at
// module load — a new MAP_PERSONA_KEYS value without a style entry throws here,
// and the map-personas.spec asserts it too).
export const MAP_PERSONA_BY_KEY: Record<MapPersonaKey, MapPersona> = Object.freeze(
  MAP_PERSONA_KEYS.reduce(
    (acc, key) => {
      const persona = MAP_PERSONAS.find((p) => p.key === key);
      if (!persona) throw new Error(`MAP_PERSONAS is missing an entry for '${key}'`);
      acc[key] = persona;
      return acc;
    },
    {} as Record<MapPersonaKey, MapPersona>,
  ),
);

/** A persona-shaped, persona-coloured marker glyph (SVG). `size` is the pixel
 *  height of the pin. */
export function PersonaMarkerIcon({
  shape,
  color,
  size = 26,
}: {
  shape: MarkerShape;
  color: string;
  size?: number;
}): React.ReactElement {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'aria-hidden': true,
  } as const;
  const stroke = '#ffffff';
  if (shape === 'person') {
    // Circle pin with a person glyph.
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="11" fill={color} stroke={stroke} strokeWidth="2" />
        <circle cx="12" cy="9.5" r="2.6" fill={stroke} />
        <path d="M6.5 17.5c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" fill={stroke} />
      </svg>
    );
  }
  if (shape === 'building') {
    // Office building: body + a window grid + a door (reads as a building, not a
    // pause symbol).
    return (
      <svg {...common}>
        <rect x="3.5" y="2.5" width="17" height="19" rx="2" fill={color} stroke={stroke} strokeWidth="2" />
        <g fill={stroke}>
          <rect x="6.5" y="6" width="3.2" height="3.2" />
          <rect x="14.3" y="6" width="3.2" height="3.2" />
          <rect x="6.5" y="11" width="3.2" height="3.2" />
          <rect x="14.3" y="11" width="3.2" height="3.2" />
          <rect x="9.8" y="16" width="4.4" height="5.5" />
        </g>
      </svg>
    );
  }
  // house: pentagon (square + triangle roof).
  return (
    <svg {...common}>
      <path d="M12 1.5 22.5 10v11a1.5 1.5 0 0 1-1.5 1.5H3A1.5 1.5 0 0 1 1.5 21V10Z" fill={color} stroke={stroke} strokeWidth="2" strokeLinejoin="round" />
      <rect x="9.5" y="13" width="5" height="7.5" fill={stroke} />
    </svg>
  );
}
